import { ActionReducerMapBuilder, createSelector, createSlice } from '@reduxjs/toolkit';

import { RootState } from 'src/store';
import { Status as StatusTypes } from 'src/ts/enums';
import { DisputeCalculationPayload } from 'src/ts/interfaces/incomeVerification';
import { ERROR_WARNING_MAPPINGS, NAME } from './constants';
import { calculateIncomeThunk, disputeCalculationThunk } from './services';

type IncomeCalculationSuccessResponse = {
  results: {
    status_code: string;
    monthly_income: number;
    reason_codes: number[];
    document_errors?: Record<string, number[]>;
  };
};

type IncomeCalculationFailedResponse = {
  status_code: string;
  reason_codes: number[];
  document_errors?: Record<string, number[]>;
};

// Combined-endpoint shape. All readable fields nested under `results`
// per FVS schema (financial-verification-services combined-income-calculation.ts:82-95).
type IncomeCalculationCombinedResponse = {
  results: {
    reference_id: string;
    status: 'ok' | 'warning' | 'error';
    gross_monthly_income?: number;
    gross_yearly_income?: number;
    reason_codes: number[];
    selected_category: 'PAY' | 'BANK' | 'TAX';
    fallback_used: boolean;
    fallback_reason: string | null;
    all_documents: Array<{
      document_id: string;
      document_type: string;
      category: 'PAY' | 'BANK' | 'TAX';
      used_in_final: boolean;
      reason_codes: number[];
    }>;
    unresolved_calculation_ids: Array<{
      calculation_id: string;
      category: 'PAY' | 'BANK' | 'TAX';
      document_type: string;
    }>;
  };
};

// `selected_category` is the FVS combined-endpoint discriminator (per
// CombinedIncomeCalculationSuccessResponseSchema in
// financial-verification-services/src/schemas/combined-income-calculation.ts).
// FVS never emits `selected_source`; the API side reads `selected_category` too.
const isCombinedResponse = (data: unknown): data is IncomeCalculationCombinedResponse => {
  if (typeof data !== 'object' || data === null || !('results' in data)) return false;
  const results = (data as { results: unknown }).results;
  if (typeof results !== 'object' || results === null) return false;

  // `'selected_category' in results` alone is true for a present-but-null key, so
  // require a string value; and structurally exclude the legacy success shape
  // (whose results carry status_code) so a legacy response that incidentally
  // gains a selected_category field can never be misrouted through combined
  // normalization.
  return (
    typeof (results as Record<string, unknown>).selected_category === 'string' &&
    !('status_code' in results)
  );
};

// Union of every shape the calculate-income thunk can resolve with: the legacy
// 200 success body, the legacy 400 error body (preserved branch), and the
// combined-endpoint body.
export type IncomeCalculationResponse =
  | IncomeCalculationSuccessResponse
  | IncomeCalculationFailedResponse
  | IncomeCalculationCombinedResponse;

type StateData = {
  calculateIncome: {
    data?: IncomeCalculationResponse | null;
    error?: string | null;
    status: StatusTypes;
  };
  disputes: {
    data: DisputeCalculationPayload | null;
    status: StatusTypes;
    error?: string | null;
  };
};

const initialState: StateData = {
  calculateIncome: {
    data: null,
    error: null,
    status: StatusTypes.IDLE
  },
  disputes: {
    data: null,
    status: StatusTypes.IDLE,
    error: null
  }
};

const incomeVerificationSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    resetIncomeVerification: () => initialState
  },
  extraReducers: (builder: ActionReducerMapBuilder<StateData>) => {
    builder.addCase(calculateIncomeThunk.pending, (state) => {
      state.calculateIncome.data = null;
      state.calculateIncome.error = null;
      state.calculateIncome.status = StatusTypes.LOADING;
    });
    builder.addCase(calculateIncomeThunk.fulfilled, (state, action) => {
      state.calculateIncome.data = action.payload;
      state.calculateIncome.status = StatusTypes.SUCCESS;
    });
    builder.addCase(calculateIncomeThunk.rejected, (state, action) => {
      // action.payload is typed via createAsyncThunk's rejectValue generic
      // — no cast needed. Shape: { code: string; message: string } | undefined.
      const payload = action.payload;
      state.calculateIncome.error = payload?.message ?? action.error?.message;
      state.calculateIncome.status = StatusTypes.ERROR;
    });

    builder.addCase(disputeCalculationThunk.pending, (state) => {
      state.disputes.data = null;
      state.disputes.error = null;
      state.disputes.status = StatusTypes.LOADING;
    });
    builder.addCase(disputeCalculationThunk.fulfilled, (state, action) => {
      state.disputes.data = action.payload.data as DisputeCalculationPayload;
      state.disputes.status = StatusTypes.SUCCESS;
    });
    builder.addCase(disputeCalculationThunk.rejected, (state, action) => {
      // NOTE: disputeCalculationThunk has no rejectValue generic; reading
      // action.error (not action.payload) is intentional. If a rejectWithValue
      // is ever added, update this handler to read action.payload.
      state.disputes.error = action.error?.message;
      state.disputes.status = StatusTypes.ERROR;
    });
  }
});

const reasonMessages = (reasons: number[], documentErrors?: Record<string, number[]>) => {
  // Merges document level errors with submission level error reasons and removes duplicates
  const uniqueErrorCodes = [
    ...new Set([
      ...reasons.filter((code) => code in ERROR_WARNING_MAPPINGS && code !== 2010), // Filter out unknown and 2010 error codes
      ...Object.values(documentErrors || {}).flat() // Flatten the array of arrays
    ])
  ];
  return uniqueErrorCodes.map((code: number) => ERROR_WARNING_MAPPINGS[code]);
};

const selectCalculateIncomeDataMemoized = createSelector(
  [(state: RootState) => state[NAME].calculateIncome.data],
  (data) => {
    if (!data) return null;

    if (isCombinedResponse(data)) {
      const r = data.results;
      const documentErrorsRecord: Record<string, number[]> = {};
      for (const doc of r.all_documents) {
        if (doc.reason_codes.length > 0) {
          documentErrorsRecord[doc.document_id] = doc.reason_codes;
        }
      }
      // r.unresolved_calculation_ids is intentionally not surfaced to the UI —
      // per-ID warnings are logged on the backend (income_verifications.ex
      // log_unresolved_calculation_ids/3). No analyst action is taken on these.
      return {
        status: r.status,
        data: r.gross_monthly_income !== undefined ? r.gross_monthly_income.toFixed(2) : undefined,
        reasons: reasonMessages(r.reason_codes ?? [], documentErrorsRecord),
        reason_codes: r.reason_codes ?? [],
        document_errors: documentErrorsRecord
      };
    }

    if ('results' in data) {
      return {
        status: data.results?.status_code,
        data:
          data.results?.monthly_income != null ? data.results.monthly_income.toFixed(2) : undefined,
        reasons: reasonMessages(data.results?.reason_codes ?? [], data.results?.document_errors),
        reason_codes: data.results?.reason_codes ?? [],
        document_errors: data.results?.document_errors ?? {}
      };
    }

    if ('status_code' in data) {
      return {
        status: data.status_code,
        reasons: reasonMessages(data.reason_codes ?? [], data.document_errors),
        reason_codes: data.reason_codes,
        document_errors: data.document_errors ?? {}
      };
    }

    // Exhaustiveness check: adding a fourth member to IncomeCalculationResponse
    // (e.g. a future OLV combined shape) fails compilation here instead of
    // silently mismatching shapes at runtime.
    const _exhaustive: never = data;
    return _exhaustive;
  }
);

export const selectors = {
  selectCalculateIncome: (state: RootState) => state[NAME].calculateIncome,
  selectCalculateIncomeData: selectCalculateIncomeDataMemoized,
  selectCalculateIncomeError: (state: RootState) => state[NAME].calculateIncome.error,
  selectCalculateIncomeStatus: (state: RootState) => state[NAME].calculateIncome.status,
  isIdle: (state: RootState) => state[NAME].calculateIncome.status === StatusTypes.IDLE,
  isLoading: (state: RootState) => state[NAME].calculateIncome.status === StatusTypes.LOADING,
  isSuccess: (state: RootState) => state[NAME].calculateIncome.status === StatusTypes.SUCCESS,
  isError: (state: RootState) => state[NAME].calculateIncome.status === StatusTypes.ERROR,
  isDisputeLoading: (state: RootState) => state[NAME].disputes.status === StatusTypes.LOADING
};

export const { reducer, actions } = incomeVerificationSlice;
